import { getGasRSIChart } from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import CustomTooltip from "$/lib/components/graphs/LineGraph/CustomTooltip";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import { RadioButtonSet } from "$/lib/components/molecules/RadioButtonSet";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  CONTAINER_HEIGHT_PERCENTAGE,
  generateGraphDateRangeFromData,
  generateXAxisTicks,
  generateYAxisTicks,
  handleGenerateYearReferences,
  handleOnChangeDefaultGraphsTabs,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { customColors } from "tailwind.config";
import { useScreen } from "usehooks-ts";

const AREA_KEYS = ["ZONE_1", "ZONE_2", "ZONE_3", "ZONE_4"];
const AREA_COLORS: Record<(typeof AREA_KEYS)[number], string> = {
  ZONE_1: "#e1a3a0",
  ZONE_2: "#f2d5a9",
  ZONE_3: "#a9bcb8",
  ZONE_4: "#8a9cc2",
};

const AREA_LABELS: Record<(typeof AREA_KEYS)[number], string> = {
  ZONE_1: "Zone de sur-vente",
  ZONE_2: "Zone neutre vendeuse",
  ZONE_3: "Zone neutre acheteuse",
  ZONE_4: "Zone de sur-achat",
};

const COLORS: Record<string, string> = {
  "PEG - Baisse moyenne 14j": "#d45359",
  "PEG - Hausse moyenne 14j": "#64a34b",
  "TTF - Baisse moyenne 14j": "#d45359",
  "TTF - Hausse moyenne 14j": "#64a34b",
  "PEG - Cal +1": "#2a6d34",
  "PEG - Cal +2": "#65a551",
  "PEG - Cal +3": "#b1c456",
  "TTF - Cal +1": "#a3013a",
  "TTF - Cal +2": "#e56453",
  "TTF - Cal +3": "#f09883",
};

const GasAnalysisRSIChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const { height: screenHeight } = useScreen();

  const graphHeight = useMemo(
    () => Math.round(screenHeight * CONTAINER_HEIGHT_PERCENTAGE) * (48 / 100),
    [screenHeight],
  );

  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    peg: boolean;
    ttf: boolean;
  }>({
    peg: true,
    ttf: false,
  });

  const [dateDiff, setDateDiff] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-rsi-chart", dateRange, dateDiff],
    queryFn: () =>
      getGasRSIChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        dateDiff,
      }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.peg && filters.ttf
          ? data.mergedProducts
          : filters.peg
            ? data.pegProducts
            : filters.ttf
              ? data.ttfProducts
              : []
        : [],
    [data, filters],
  );

  const keys = useMemo(
    () => getArrayKeys(toDisplay).filter((key) => !AREA_KEYS.includes(key)),
    [toDisplay],
  );

  const lineKeys = useMemo(
    () =>
      getArrayKeys(toDisplay, "date").filter(
        (key) => !AREA_KEYS.includes(key) && !key.includes("RSI"),
      ),
    [toDisplay],
  );

  const result = getMinMax(
    toDisplay,
    keys.filter((key) => !key.includes("Cal") && !key.includes("RSI")),
  );
  const resultCal = getMinMax(
    toDisplay,
    keys.filter((key) => key.includes("Cal") && !key.includes("RSI")),
  );

  const dataDateRange = useMemo(
    () => generateGraphDateRangeFromData(toDisplay, "dd/MM/yy", "date"),
    [toDisplay],
  );

  const xDomainRSI = useMemo(
    () =>
      dataDateRange && dataDateRange[0] && dataDateRange[1]
        ? generateXAxisTicks(dataDateRange[0], dataDateRange[1])
        : undefined,
    [dataDateRange],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      lineKeys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: COLORS[key],
        }),
        {},
      ),
    [lineKeys],
  );

  const [references] = useMemo(() => {
    const years = handleGenerateYearReferences(toDisplay, "date", "dd/MM/yy");

    return [years];
  }, [toDisplay]);

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const visibleKeys = useMemo(
    () =>
      graphVisibility
        ? graphVisibility.filter((item) => item.name !== "RSI")
        : keys.map((key) => ({
            name: key,
            isVisible: true,
          })),
    [graphVisibility, keys],
  );

  const hideKeys = useMemo(
    () =>
      visibleKeys.reduce<string[]>((acc, item) => {
        if (item.isVisible === false) return [...acc, item.name];

        return acc;
      }, []),
    [visibleKeys],
  );

  // const notVisibleKeys = (graphVisibility ?? [])
  //   .filter(
  //     (item) =>
  //       item.name !== "RSI" && !item.isVisible && item.isVisible !== undefined,
  //   )
  //   .map((item) => item.name);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      lineKeys.map((key) => ({
        name: key,
        isVisible: true,
        color: COLORS[key],
      })),
    );
  }, [lineKeys, filters]);

  return (
    <DisplayBox
      title="RSI - Cal+1 à Cal+3"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth className="gap-4">
            <ResponsiveContainer width="100%" height={graphHeight}>
              <ComposedChart
                data={toDisplay}
                margin={{ top: 10, right: 84, left: -30, bottom: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickCount={10}
                  fontSize={12}
                  tickMargin={5}
                  interval={0}
                  ticks={xDomainRSI}
                />

                <YAxis
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  domain={[0, 100]}
                  tickMargin={5}
                  label={{
                    value: "RSI",
                    angle: -90,
                    position: "center",
                  }}
                />

                <Tooltip
                  content={
                    <CustomTooltip unit="" theme="green" hideKeys={AREA_KEYS} />
                  }
                />

                {AREA_KEYS.map((key) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stackId="1"
                    stroke={AREA_COLORS[key]}
                    fill={AREA_COLORS[key]}
                    fillOpacity={1}
                    tooltipType="none"
                    activeDot={false}
                    connectNulls
                  />
                ))}

                <Line
                  type="step"
                  dataKey={filters.peg ? "PEG - RSI" : "TTF - RSI"}
                  stroke={"black"}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
                {filters.peg && filters.ttf && (
                  <Line
                    type="step"
                    dataKey="TTF - RSI"
                    stroke={"#784b7a"}
                    dot={false}
                    strokeWidth={2}
                    connectNulls
                  />
                )}

                {/* This is the same chart as the first one but rendered here with 0 opacity and labels in order to have the labels above the line */}
                {AREA_KEYS.map((key) => (
                  <Area
                    key={key}
                    dataKey={key}
                    stackId="2"
                    stroke="none"
                    fill="none"
                    fillOpacity={0}
                    tooltipType="none"
                    connectNulls
                  >
                    <LabelList
                      position="top"
                      dataKey={key}
                      content={({ x, y, index }) => {
                        const xPos = Number(x) + 5;
                        const yPos = Number(y) + 5;

                        if (index === 0)
                          return (
                            <g>
                              <rect
                                x={xPos}
                                y={yPos}
                                width={130}
                                height={20}
                                fill="white"
                                opacity={0.5}
                              />
                              <text
                                x={xPos + 5}
                                y={yPos + 11}
                                textAnchor="start"
                                fill="black"
                                fontSize={12}
                                dominantBaseline="middle"
                              >
                                {AREA_LABELS[key]}
                              </text>
                            </g>
                          );

                        return <></>;
                      }}
                      fontSize={12}
                      fill="white"
                    />
                  </Area>
                ))}
              </ComposedChart>
            </ResponsiveContainer>

            <ResponsiveContainer width="100%" height={graphHeight}>
              <ComposedChart
                data={toDisplay}
                margin={{ top: 10, right: 0, left: -30, bottom: 0 }}
              >
                {references.map(({ label }) => (
                  <ReferenceLine
                    key={label}
                    x={label}
                    stroke={customColors.tertiary.DEFAULT}
                    strokeDasharray={5}
                  />
                ))}

                <XAxis
                  dataKey="date"
                  fontSize={12}
                  interval={0}
                  ticks={xDomainRSI}
                />

                <YAxis
                  yAxisId={0}
                  tickSize={2}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  orientation="left"
                  domain={[result.min.value, result.max.value]}
                  interval={0}
                  tickFormatter={numberFormatter}
                  ticks={generateYAxisTicks(result.min.value, result.max.value)}
                  label={{
                    value: "Moyennes des hausses & baisses",
                    angle: -90,
                    position: "center",
                  }}
                />
                <YAxis
                  yAxisId={1}
                  tickSize={2}
                  dataKey={keys.find((key) => key.includes("Cal"))}
                  width={85}
                  fontWeight={500}
                  fontSize={12}
                  tickMargin={5}
                  orientation="right"
                  interval={0}
                  tickFormatter={numberFormatter}
                  domain={[resultCal.min.value, resultCal.max.value]}
                  unit="€/MWh"
                />

                <Tooltip
                  content={
                    <CustomTooltip
                      unit=""
                      hideKeys={hideKeys}
                      displayUnits={{
                        [`${keys.find((key) => key.includes("Cal"))}`]:
                          " €/MWh",
                      }}
                    />
                  }
                />

                {visibleKeys.map(({ name, isVisible }) => (
                  <Line
                    key={name}
                    yAxisId={name.includes("Cal") ? 1 : 0}
                    type="monotone"
                    dataKey={name}
                    stroke={
                      colors && colors[name]
                        ? colors[name]
                        : customColors.primary.DEFAULT
                    }
                    dot={false}
                    activeDot={false}
                    strokeDasharray={name.includes("Cal") ? "5 5" : undefined}
                    strokeWidth={isVisible === undefined || isVisible ? 2 : 0}
                    connectNulls
                  />
                ))}

                {/* {graphVisibility &&
                notVisibleKeys.map((key) => (
                  <Line
                    yAxisId={key.includes("Cal") ? 1 : 0}
                    type="monotone"
                    dataKey={key}
                    stroke={customColors.tertiary.DEFAULT}
                    strokeOpacity={0.5}
                    dot={false}
                    activeDot={false}
                  />
                ))} */}
              </ComposedChart>
            </ResponsiveContainer>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <CheckboxSet<"peg" | "ttf">
            id="gas-market-rsi-chart"
            title="Marché"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.peg && !newValue.ttf) return prev;
                return newValue;
              })
            }
            options={[
              { id: "peg", label: "PEG" },
              { id: "ttf", label: "TTF" },
            ]}
          />

          <RadioButtonSet<number>
            id="gas-elec-date-diff-rsi"
            title="Produit calendaire"
            selectedId={dateDiff}
            onChange={(id) => setDateDiff(id)}
            options={[
              { id: 1, label: "Cal +1" },
              { id: 2, label: "Cal +2" },
              { id: 3, label: "Cal +3" },
            ]}
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasAnalysisRSIChart;
